import {
  Calculator, FilePlus, Save, CheckCircle2, History, MoreHorizontal,
  ChevronDown, ChevronRight, Check, Copy, Send, Mail, FileDown,
  ClipboardList, FileText, ExternalLink, AlertTriangle, FileStack, FolderOpen,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";

export function QuotePageControls({
  jobId,
  jobNumber,
  job,
  quote,
  isSaving,
  isDuplicating,
  isCreatingSnapshot,
  isSendingQuote,
  isEmailingQuote,
  isCreatingXeroInvoice,
  hasApprovedSnapshot,
  hasUnsavedChanges,
  lastSaved,
  latestSnapshot,
  approvalInfo,
  quoteSentInfo,
  quoteEmailLog,
  xeroConnected,
  xeroInvoice,
  gmailConnected,
  isApproved,
  isSent,
  // Actions
  startNewJob,
  saveJob,
  duplicateJob,
  createSnapshot,
  sendQuoteToClient,
  markAsSent,
  emailQuote,
  downloadPDF,
  downloadProductionSheet,
  createXeroInvoice,
  // Template actions
  openSaveAsTemplate,
  openApplyTemplate,
  // Send Quote Email
  openSendQuoteEmail,
}) {
  // Format last saved time
  const formatLastSaved = () => {
    if (!lastSaved) return null;
    const now = new Date();
    const diff = now - lastSaved;
    const minutes = Math.floor(diff / 60000);
    if (minutes < 1) return "Just now";
    if (minutes === 1) return "1 min ago";
    if (minutes < 60) return `${minutes} mins ago`;
    return lastSaved.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };
  
  return (
    <div className="bg-white border-b border-gray-200">
      <div className="max-w-aa-container mx-auto px-6 py-4">
        {/* Top: Job Context + Primary Actions */}
        <div className="flex items-center justify-between gap-4">
          {/* Left: Job Info */}
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="p-2.5 rounded-aa-md bg-aa-primary/10 shrink-0">
              <Calculator className="w-5 h-5 text-aa-primary" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                {jobNumber ? (
                  <>
                    <span className="px-2 py-0.5 bg-aa-bg rounded-aa-sm font-mono text-aa-xs text-gray-600 shrink-0">
                      {jobNumber}
                    </span>
                    <h1 className="text-aa-lg font-bold text-gray-900 truncate">
                      {job.job_name || "Untitled Job"}
                    </h1>
                  </>
                ) : (
                  <h1 className="text-aa-lg font-bold text-gray-900">New Quote</h1>
                )}
                {job.status && (
                  <span 
                    className={`shrink-0 px-2 py-0.5 text-aa-xs font-medium rounded-aa-sm border ${
                      job.status === "Draft" ? "bg-amber-50 text-amber-700 border-amber-300" :
                      job.status === "Approved" ? "bg-aa-success/10 text-aa-success border-aa-success/30" :
                      job.status === "Sent" ? "bg-purple-50 text-purple-700 border-purple-300" :
                      job.status === "In Production" ? "bg-aa-primary/10 text-aa-primary border-aa-primary/30" :
                      job.status === "Complete" ? "bg-emerald-50 text-emerald-700 border-emerald-300" :
                      "bg-gray-100 text-gray-600 border-gray-300"
                    }`}
                    data-testid="job-status-badge"
                  >
                    {job.status}
                  </span>
                )}
                {hasUnsavedChanges && (
                  <span className="shrink-0 px-2 py-0.5 text-aa-xs font-medium rounded-aa-sm bg-orange-100 text-orange-700 border border-orange-300" data-testid="unsaved-changes-badge">
                    Unsaved
                  </span>
                )}
              </div>
              <p className="text-aa-xs text-gray-500 mt-0.5 hidden sm:block">
                {approvalInfo 
                  ? `Approved by ${approvalInfo.approved_by} on ${new Date(approvalInfo.approved_at).toLocaleDateString()}`
                  : quoteSentInfo 
                  ? `Sent ${new Date(quoteSentInfo.sent_at).toLocaleDateString()}`
                  : lastSaved
                  ? `Saved ${formatLastSaved()}`
                  : "Business Quote Builder"
                }
              </p>
            </div>
          </div>

          {/* Right: Primary Actions */}
          <div className="flex items-center gap-2 shrink-0">
            {/* New */}
            <button
              onClick={startNewJob}
              data-testid="new-job-btn"
              className="flex items-center gap-1.5 px-3 py-2 rounded-aa-md bg-aa-card shadow-aa-button text-aa-xs font-medium transition-all hover:-translate-y-0.5 active:shadow-aa-inset"
            >
              <FilePlus className="w-4 h-4" />
              <span className="hidden sm:inline">New</span>
            </button>
            
            {/* Save */}
            <button 
              onClick={saveJob} 
              disabled={isSaving}
              data-testid="save-job-btn"
              className={`flex items-center gap-1.5 px-3 py-2 rounded-aa-md text-white text-aa-xs font-medium transition-all hover:-translate-y-0.5 disabled:opacity-50 ${
                hasUnsavedChanges 
                  ? "bg-orange-500 hover:shadow-[0_4px_12px_rgba(249,115,22,0.3)]" 
                  : "bg-aa-primary hover:shadow-[0_4px_12px_rgba(30,58,95,0.3)]"
              }`}
            >
              <Save className="w-4 h-4" />
              <span className="hidden sm:inline">
                {isSaving ? "Saving..." : jobId ? (job.status === "Draft" ? "Save Draft" : "Save") : "Save Draft"}
              </span>
            </button>
            
            {/* Approve - Primary CTA when applicable */}
            {jobId && !hasApprovedSnapshot && (
              <button
                onClick={() => createSnapshot(false)}
                disabled={isCreatingSnapshot || isSaving}
                data-testid="mark-approved-btn"
                className="flex items-center gap-1.5 px-3 py-2 rounded-aa-md bg-aa-success text-white text-aa-xs font-medium transition-all hover:shadow-[0_4px_12px_rgba(34,197,94,0.3)] hover:-translate-y-0.5 disabled:opacity-50"
              >
                <CheckCircle2 className="w-4 h-4" />
                <span className="hidden sm:inline">{isCreatingSnapshot ? "..." : "Approve"}</span>
              </button>
            )}
            
            {/* Revision - When approved and has changes */}
            {jobId && hasApprovedSnapshot && hasUnsavedChanges && (
              <button
                onClick={() => createSnapshot(true)}
                disabled={isCreatingSnapshot || isSaving}
                data-testid="create-revision-btn"
                className="flex items-center gap-1.5 px-3 py-2 rounded-aa-md bg-aa-warning text-white text-aa-xs font-medium transition-all hover:shadow-[0_4px_12px_rgba(245,158,11,0.3)] hover:-translate-y-0.5 disabled:opacity-50"
              >
                <History className="w-4 h-4" />
                <span className="hidden sm:inline">{isCreatingSnapshot ? "..." : "Revision"}</span>
              </button>
            )}

            {/* More Actions Dropdown - Always visible with clean disabled states */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button 
                  data-testid="more-actions-btn" 
                  className="flex items-center gap-1.5 px-3 py-2 rounded-aa-md bg-aa-card shadow-aa-button text-aa-xs font-medium transition-all hover:-translate-y-0.5 active:shadow-aa-inset"
                >
                  <MoreHorizontal className="w-4 h-4" />
                  <span className="hidden sm:inline">More</span>
                  <ChevronDown className="w-3 h-3" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                {/* Templates Section */}
                <DropdownMenuItem 
                  onClick={openApplyTemplate}
                  data-testid="apply-template-item"
                >
                  <FolderOpen className="w-4 h-4 mr-2" />
                  Apply Template
                </DropdownMenuItem>
                
                {jobId && (job.garments?.length > 0 || job.artwork_groups?.length > 0) ? (
                  <DropdownMenuItem 
                    onClick={openSaveAsTemplate}
                    data-testid="save-as-template-item"
                  >
                    <FileStack className="w-4 h-4 mr-2" />
                    Save as Template
                  </DropdownMenuItem>
                ) : !jobId ? (
                  <DropdownMenuItem disabled className="text-gray-400">
                    <FileStack className="w-4 h-4 mr-2 opacity-50" />
                    Save as Template (save first)
                  </DropdownMenuItem>
                ) : null}
                
                <DropdownMenuSeparator />
                
                {/* Duplicate */}
                {jobId && (
                  <DropdownMenuItem 
                    onClick={duplicateJob}
                    disabled={isDuplicating}
                    data-testid="duplicate-job-item"
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    {isDuplicating ? "Duplicating..." : "Duplicate as Reorder"}
                  </DropdownMenuItem>
                )}
                
                <DropdownMenuSeparator />
                
                {/* Send Quote via Email (Primary action) */}
                {jobId && gmailConnected && !isApproved && (
                  <DropdownMenuItem 
                    onClick={openSendQuoteEmail}
                    data-testid="send-quote-email-item"
                    className="font-medium"
                  >
                    <Mail className="w-4 h-4 mr-2 text-aa-primary" />
                    Send Quote
                  </DropdownMenuItem>
                )}
                
                {/* Send Quote Link (Fallback if no Gmail) */}
                {jobId && !gmailConnected && job.client?.email && !isApproved && !isSent && (
                  <DropdownMenuItem 
                    onClick={sendQuoteToClient}
                    disabled={isSendingQuote}
                  >
                    <Send className="w-4 h-4 mr-2" />
                    {isSendingQuote ? "Sending..." : "Send Quote Link"}
                  </DropdownMenuItem>
                )}
                
                {!jobId && (
                  <DropdownMenuItem disabled className="text-gray-400">
                    <Mail className="w-4 h-4 mr-2 opacity-50" />
                    Send Quote (save first)
                  </DropdownMenuItem>
                )}
                
                {/* Mark as Sent (manual fallback) */}
                {jobId && job.status === "Draft" && (
                  <DropdownMenuItem 
                    onClick={markAsSent}
                    disabled={isSendingQuote}
                    data-testid="mark-sent-item"
                    className="text-gray-600"
                  >
                    <Check className="w-4 h-4 mr-2" />
                    Mark as Sent (manual)
                  </DropdownMenuItem>
                )}
                
                {/* Email Quote (for Approved jobs) */}
                {job.status === "Approved" && gmailConnected && hasApprovedSnapshot && latestSnapshot?.client_email ? (
                  <DropdownMenuItem 
                    onClick={() => emailQuote(quoteEmailLog ? true : false)}
                    disabled={isEmailingQuote}
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    {isEmailingQuote ? "Sending..." : quoteEmailLog ? "Resend Quote Email" : "Email Quote PDF"}
                  </DropdownMenuItem>
                ) : job.status === "Approved" && !gmailConnected ? (
                  <DropdownMenuItem disabled className="text-gray-400">
                    <Mail className="w-4 h-4 mr-2 opacity-50" />
                    Email (connect Gmail first)
                  </DropdownMenuItem>
                ) : null}
                
                <DropdownMenuSeparator />
                
                {/* Download PDF */}
                {(quote || hasApprovedSnapshot) ? (
                  <DropdownMenuItem onClick={downloadPDF} data-testid="download-pdf-item">
                    <FileDown className="w-4 h-4 mr-2" />
                    Download Quote PDF
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem disabled className="text-gray-400">
                    <FileDown className="w-4 h-4 mr-2 opacity-50" />
                    Download PDF (add items first)
                  </DropdownMenuItem>
                )}
                
                {/* Production Sheet */}
                {job.status !== "Draft" ? (
                  <DropdownMenuItem onClick={downloadProductionSheet} data-testid="production-sheet-item">
                    <ClipboardList className="w-4 h-4 mr-2" />
                    Production Sheet
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem disabled className="text-gray-400">
                    <ClipboardList className="w-4 h-4 mr-2 opacity-50" />
                    Production Sheet (approve first)
                  </DropdownMenuItem>
                )}
                
                {/* Xero Invoice */}
                {job.status === "Approved" && xeroConnected && hasApprovedSnapshot ? (
                  <DropdownMenuItem 
                    onClick={() => createXeroInvoice(xeroInvoice ? true : false)} 
                    disabled={isCreatingXeroInvoice}
                    data-testid="create-xero-invoice-item"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    {xeroInvoice ? "Create Another Invoice" : "Create Xero Invoice"}
                  </DropdownMenuItem>
                ) : xeroConnected ? (
                  <DropdownMenuItem disabled className="text-gray-400">
                    <FileText className="w-4 h-4 mr-2 opacity-50" />
                    Xero Invoice (approve first)
                  </DropdownMenuItem>
                ) : null}
                
                {/* Copy Approval Link */}
                {(quoteSentInfo?.public_url || (isSent && jobId)) && !isApproved && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem 
                      onClick={() => {
                        const url = quoteSentInfo?.public_url || `${window.location.origin}/public/quote/${job.quote_token}`;
                        navigator.clipboard.writeText(url);
                        toast.success("Approval link copied!");
                      }}
                      data-testid="copy-link-item"
                    >
                      <Copy className="w-4 h-4 mr-2" />
                      Copy Approval Link
                    </DropdownMenuItem>
                  </>
                )}
                
                {/* Xero Invoice Link */}
                {xeroInvoice && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <a
                        href={xeroInvoice.invoice_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center"
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        View Invoice {xeroInvoice.invoice_number}
                      </a>
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        
        {/* Bottom: Progress Steps (inline, not a separate row) */}
        <ProgressSteps 
          garmentsCount={job.garments.length}
          artworkGroupsCount={job.artwork_groups.length}
          hasQuote={!!quote}
          isSent={isSent}
          isApproved={isApproved}
        />
      </div>
      
      {/* Warning banner - only when there are unsaved changes to approved quote */}
      {hasApprovedSnapshot && hasUnsavedChanges && (
        <div className="bg-aa-warning/10 border-t border-aa-warning/30 px-6 py-2 flex items-center justify-center gap-2 text-aa-warning text-aa-xs font-medium">
          <AlertTriangle className="w-4 h-4" />
          <span>You have unsaved changes. Saving will create a new revision.</span>
        </div>
      )}
    </div>
  );
}

function ProgressSteps({ garmentsCount, artworkGroupsCount, hasQuote, isSent, isApproved }) {
  return (
    <div className="flex items-center justify-center gap-2 mt-4 pt-4 border-t border-gray-100" data-testid="progress-indicator">
      {/* Step 1: Garments */}
      <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-aa-md text-aa-xs font-medium ${
        garmentsCount > 0 ? "bg-aa-success/10 text-aa-success" : "bg-gray-100 text-gray-500"
      }`}>
        <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
          garmentsCount > 0 ? "bg-aa-success text-white" : "bg-gray-300 text-white"
        }`}>1</span>
        <span className="hidden sm:inline">Garments</span>
        {garmentsCount > 0 && <Check className="w-3.5 h-3.5" />}
      </div>
      
      <ChevronRight className="w-4 h-4 text-gray-300" />
      
      {/* Step 2: Prints */}
      <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-aa-md text-aa-xs font-medium ${
        artworkGroupsCount > 0 ? "bg-aa-success/10 text-aa-success" : "bg-gray-100 text-gray-500"
      }`}>
        <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
          artworkGroupsCount > 0 ? "bg-aa-success text-white" : "bg-gray-300 text-white"
        }`}>2</span>
        <span className="hidden sm:inline">Prints</span>
        {artworkGroupsCount > 0 && <Check className="w-3.5 h-3.5" />}
      </div>
      
      <ChevronRight className="w-4 h-4 text-gray-300" />
      
      {/* Step 3: Pricing */}
      <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-aa-md text-aa-xs font-medium ${
        hasQuote ? "bg-aa-success/10 text-aa-success" : "bg-gray-100 text-gray-500"
      }`}>
        <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
          hasQuote ? "bg-aa-success text-white" : "bg-gray-300 text-white"
        }`}>3</span>
        <span className="hidden sm:inline">Pricing</span>
        {hasQuote && <Check className="w-3.5 h-3.5" />}
      </div>
      
      <ChevronRight className="w-4 h-4 text-gray-300" />
      
      {/* Step 4: Sent/Approved */}
      <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-aa-md text-aa-xs font-medium ${
        isSent || isApproved ? "bg-aa-success/10 text-aa-success" : "bg-gray-100 text-gray-500"
      }`}>
        <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
          isSent || isApproved ? "bg-aa-success text-white" : "bg-gray-300 text-white"
        }`}>4</span>
        <span className="hidden sm:inline">{isApproved ? "Approved" : "Sent"}</span>
        {(isSent || isApproved) && <Check className="w-3.5 h-3.5" />}
      </div>
    </div>
  );
}

export default QuotePageControls;
